import { Column } from '@tanstack/react-table';
import { BaseSortType, baseSortTypes } from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { HTMLAttributes, MouseEvent, ReactNode, useRef } from 'react';
import { Button } from '../../../../base/button/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../../base/dropdown-menu';
import { NoddiTooltip } from '../../../Overlay/NoddiTooltip';
import { resolveTableColumnHeaderTranslations, TableColumnHeaderTranslations } from '../tableTranslationTypes';

interface Props<TData, TValue> extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  column: Column<TData, TValue>;
  title: ReactNode;
  description?: ReactNode;
  descriptionClassName?: string;
  sortOrder?: BaseSortType;
  isSortable?: boolean;
  hideSortIcon?: boolean;
  nativeTitle?: string;
  onSortChange?: (nextOrder: BaseSortType) => void;
  translations?: Partial<TableColumnHeaderTranslations>;
}

export function TableColumnHeader<TData, TValue>({
  column,
  descriptionClassName,
  title,
  className,
  description,
  sortOrder = baseSortTypes.null,
  isSortable,
  hideSortIcon,
  nativeTitle,
  onSortChange,
  translations
}: Props<TData, TValue>) {
  const texts = resolveTableColumnHeaderTranslations(translations);
  // Radix hands focus back to the trigger when the menu closes, and the browser
  // paints the focus ring even when the sort was chosen with the mouse -- leaving
  // a box drawn around one column header. Suppress the hand-back for pointer
  // choices only, so keyboard users keep their place.
  const closedByPointer = useRef(false);

  const columnCanSort = column.getCanSort();
  const canSort = onSortChange ? isSortable : columnCanSort;
  const currentOrder: BaseSortType = onSortChange
    ? (sortOrder ?? baseSortTypes.null)
    : column.getIsSorted()
      ? (column.getIsSorted() as BaseSortType)
      : baseSortTypes.null;

  if (!canSort) {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        {title}
        {description && <NoddiTooltip content={description} />}
      </div>
    );
  }

  const handleSort = (nextOrder: BaseSortType, event: MouseEvent<HTMLDivElement>) => {
    closedByPointer.current = event.detail > 0;

    if (onSortChange) {
      onSortChange(nextOrder);
      return;
    }

    if (nextOrder === baseSortTypes.null) {
      column.clearSorting();
      return;
    }

    column.toggleSorting(nextOrder === baseSortTypes.desc);
  };

  const sortIcon =
    currentOrder === baseSortTypes.desc
      ? 'ArrowDown'
      : currentOrder === baseSortTypes.asc
        ? 'ArrowUp'
        : 'ChevronsUpDown';

  const isSorted = currentOrder !== baseSortTypes.null;

  const dropdownButton = (
    <Button
      variant='ghost'
      className={cn('-ml-3 h-8 data-[state=open]:bg-accent', isSorted && 'bg-accent text-accent-foreground')}
    >
      <span className='flex items-center gap-1'>
        {title}
        {!nativeTitle && description && (
          // need to use ! here as button has svg size-4 styling...
          <NoddiTooltip
            trigger={<NoddiIcon className='size-3!' name='Info' />}
            content={description}
            tooltipContentWrapperClassName={descriptionClassName}
          />
        )}
      </span>
      {!hideSortIcon && <NoddiIcon name={sortIcon} className={isSorted ? undefined : 'text-muted-foreground'} />}
    </Button>
  );

  const sortMenuItems = (
    <DropdownMenuContent
      align='start'
      onCloseAutoFocus={(event) => {
        if (closedByPointer.current) {
          event.preventDefault();
        }
      }}
    >
      <DropdownMenuItem onClick={(event) => handleSort(baseSortTypes.asc, event)}>
        <NoddiIcon name='ArrowUp' size={14} className='text-muted-foreground/70' />
        {texts.asc}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={(event) => handleSort(baseSortTypes.desc, event)}>
        <NoddiIcon name='ArrowDown' size={14} className='text-muted-foreground/70' />
        {texts.desc}
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={currentOrder === baseSortTypes.null}
        onClick={(event) => handleSort(baseSortTypes.null, event)}
      >
        <NoddiIcon name='RotateCcw' size={14} className='text-muted-foreground/70' />
        {texts.resetSorting}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
    </DropdownMenuContent>
  );

  if (nativeTitle) {
    return (
      // eslint-disable-next-line noddi/enforce-spacing-scale -- not migrated yet: legacy space-x-2 row, equals gap-2 on this flex row
      <div className={cn('flex items-center space-x-2', className)}>
        <TooltipPrimitive.Root>
          <DropdownMenu
            onOpenChange={(open) => {
              if (open) {
                closedByPointer.current = false;
              }
            }}
          >
            <TooltipPrimitive.Trigger asChild>
              <DropdownMenuTrigger asChild>{dropdownButton}</DropdownMenuTrigger>
            </TooltipPrimitive.Trigger>
            {sortMenuItems}
          </DropdownMenu>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              sideOffset={4}
              className='z-overlay animate-in rounded-md border bg-white px-3 py-1.5 text-xs drop-shadow-xl fade-in-0 zoom-in-95'
            >
              {nativeTitle}
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </div>
    );
  }

  return (
    // eslint-disable-next-line noddi/enforce-spacing-scale -- not migrated yet: legacy space-x-2 row, equals gap-2 on this flex row
    <div className={cn('flex items-center space-x-2', className)}>
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) {
            closedByPointer.current = false;
          }
        }}
      >
        <DropdownMenuTrigger asChild>{dropdownButton}</DropdownMenuTrigger>
        {sortMenuItems}
      </DropdownMenu>
    </div>
  );
}
